import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

// Tüm terapistleri getir
export const getTherapists = createAsyncThunk(
  "therapists/getTherapists",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_URL}/therapists`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error fetching therapists"
      );
    }
  }
);

// Arama ile terapistleri getir
export const getTherapistsSearch = createAsyncThunk(
  "therapists/getTherapistsSearch",
  async (searchParams = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (searchParams.query) queryParams.append("query", searchParams.query);
      if (searchParams.branchId) queryParams.append("branchId", searchParams.branchId);
      if (searchParams.rating) queryParams.append("rating", searchParams.rating);
      if (searchParams.order) queryParams.append("order", searchParams.order);
      if (searchParams.page) queryParams.append("page", searchParams.page);
      if (searchParams.limit) queryParams.append("limit", searchParams.limit);
      if (searchParams.year) queryParams.append("year", searchParams.year);
      if (searchParams.month) queryParams.append("month", searchParams.month);
      
      const url = `${API_URL}/therapists/search${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "There was an error fetching the therapists."
      );
    }
  }
);

// Tek bir terapist bilgisini getir
export const getTherapist = createAsyncThunk(
  "therapists/getTherapist",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/therapists/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Therapist information not found"
      );
    }
  }
);

// Terapist kaydı
export const registerTherapist = createAsyncThunk(
  "therapists/registerTherapist",
  async (therapistData, { rejectWithValue }) => {
    try {
      console.log("FormData içeriği:", [...therapistData.entries()]);
      
      const response = await axios.post(
        `${API_URL}/therapists/signup`,
        therapistData,
        {
          headers: { 
            "Content-Type": "multipart/form-data"
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error("Registration error details:", error.response?.data);
      return rejectWithValue(
        error.response?.data?.message || "Registration failed"
      );
    }
  }
);

// Terapist giriş yapma
export const loginTherapist = createAsyncThunk(
  "therapists/loginTherapist",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL}/therapists/signin`,
        credentials
      );
      
      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("userType", "therapist");
      }
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Login failed"
      );
    }
  }
);

// OTP ile telefon girişi
export const initiateTherapistOTP = createAsyncThunk(
  "therapists/initiateOTP",
  async ({ phoneNumber, countryCode }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL}/therapists/otp/initiate`,
        { phoneNumber, countryCode }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "OTP sending failed"
      );
    }
  }
);

// OTP doğrulama
export const verifyTherapistOTP = createAsyncThunk(
  "therapists/verifyOTP", 
  async ({ otpCode }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL}/therapists/otp/verify`,
        { otpCode }
      );
      
      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("userType", "therapist");
      }
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "OTP verification failed"
      );
    }
  }
);

// Telefon numarası doğrulama kodu gönderme
export const sendPhoneVerification = createAsyncThunk(
  "therapists/sendPhoneVerification",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      const response = await axios.post(
        `${API_URL}/therapists/verify-phone/send`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Verification code not sent"
      );
    }
  }
);

// Telefon doğrulama kodunu kontrol etme
export const verifyPhoneToken = createAsyncThunk(
  "therapists/verifyPhoneToken",
  async (token, { rejectWithValue }) => {
    try {
      const authToken = localStorage.getItem("token");
      if (!authToken) {
        return rejectWithValue("Session not found");
      }
      
      const response = await axios.post(
        `${API_URL}/therapists/verify-phone/check`,
        { token },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Verification code invalid"
      );
    }
  }
);

export const logoutTherapist = createAsyncThunk(
  "therapists/logout",
  async () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userType");
    return null;
  }
);

// Terapist bilgilerini güncelleme
export const updateTherapist = createAsyncThunk(
  "therapists/updateTherapist",
  async (payload, { rejectWithValue }) => {
    let requestData;
    let contentType;
    let endpoint = `${API_URL}/therapists`;
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      if (payload instanceof FormData) {
        requestData = payload;
      } else if (payload.formData instanceof FormData) {
        requestData = payload.formData;
      } else if (payload.jsonData) {
        requestData = payload.jsonData;
        contentType = 'application/json';
      } else {
        const targetEndpoint = payload?._targetEndpoint;
        if (targetEndpoint === "users") {
          endpoint = `${API_URL}/users`;
        }

        const { _targetEndpoint, ...restPayload } = payload || {};
        requestData = restPayload;
        contentType = 'application/json';
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      if (contentType) {
        headers['Content-Type'] = contentType;
      }
      
      const response = await axios.put(
        endpoint,
        requestData,
        { headers }
      );
      
      return { 
        success: true,
        therapist: response.data.therapist || response.data 
      };
    } catch (error) {
      const errData = error.response?.data;
      console.error("API error - status:", error.response?.status);
      console.error("API error - body:", JSON.stringify(errData, null, 2));
      console.error("API error - sent data:", JSON.stringify(requestData, null, 2));
      return rejectWithValue(
        errData?.message || errData?.error || JSON.stringify(errData) || "Update failed"
      );
    }
  }
);

// Diğer terapist işlemleri...
export const uploadTherapistImage = createAsyncThunk(
  "therapists/uploadTherapistImage",
  async (file) => {
    try {
      const formData = new FormData();
      formData.append("picture", file);

      const response = await axios.post(`${API_URL}/upload/image`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Image upload failed"
      );
    }
  }
);
export const sendPasswordResetLink = createAsyncThunk(
  "therapists/sendPasswordResetLink",
  async (email, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL}/therapists/forgot-password`,
        { email }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Password reset link not sent"
      );
    }
  }
);

// Yeni şifre belirleme (token ile)
export const setNewPasswordWithToken = createAsyncThunk(
  "therapists/setNewPasswordWithToken",
  async ({ id, token, password }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL}/therapists/set-new-password/${id}/${token}`,
        { password }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Password reset failed"
      );
    }
  }
);
export const uploadTherapistResume = createAsyncThunk(
  "therapists/uploadTherapistResume",
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("resume", file);

      const response = await axios.post(`${API_URL}/upload/resume`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "PDF upload failed"
      );
    }
  }
);

export const changeTherapistPassword = createAsyncThunk(
  "therapists/changeTherapistPassword",
  async (passwordData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }

      const response = await axios.put(
        `${API_URL}/therapists/password/change`,
        passwordData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Password change failed"
      );
    }
  }
);

// Mevcut giriş yapmış terapist bilgilerini getir
export const getCurrentTherapist = createAsyncThunk(
  "therapists/getCurrentTherapist",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      const response = await axios.get(`${API_URL}/therapists/current-user`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      // Sadece gerçek auth hatalarında session temizle
      if (status === 401 || status === 403) {
        localStorage.removeItem("token");
        localStorage.removeItem("userType");
      }
      return rejectWithValue(
        error.response?.data?.message || "Therapist information not found"
      );
    }
  }
);

// Admin işlemleri

// Terapist sıralamasını güncelle
export const updateTherapistOrder = createAsyncThunk(
  "therapists/updateTherapistOrder",
  async ({ therapistId, order }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return rejectWithValue("Session not found");

      const response = await axios.put(
        `${API_URL}/therapists/admin/order/${therapistId}`,
        { order },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      return { therapistId, order, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Therapist order update failed"
      );
    }
  }
);

// Terapisti onaylama
export const approveTherapist = createAsyncThunk(
  "therapists/approveTherapist",
  async (therapistId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      const response = await axios.put(
        `${API_URL}/therapists/admin/approve/${therapistId}`,
        { 
          isVerified: true,
          isApprovedByAdmin: true, 
          status: 'approved',
          approvalStatus: 'approved' 
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      return {
        therapistId,
        ...response.data
      };
    } catch (error) {
      console.error("Approval error:", error.response?.data || error.message);
      return rejectWithValue(
        error.response?.data?.message || "Therapist approval failed"
      );
    }
  }
);

// Terapisti reddetme
export const rejectTherapist = createAsyncThunk(
  "therapists/rejectTherapist", 
  async (therapistId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      const response = await axios.put(
        `${API_URL}/therapists/admin/reject/${therapistId}`,
        { 
          isVerified: false,
          isApprovedByAdmin: false, 
          status: 'rejected',
          approvalStatus: 'rejected' 
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      return {
        therapistId,
        ...response.data
      };
    } catch (error) {
      console.error("Rejection error:", error.response?.data || error.message);
      return rejectWithValue(
        error.response?.data?.message || "Therapist rejection failed"
      );
    }
  }
);

// Terapisti askıya alma / aktifleştirme (Admin only)
export const toggleTherapistActive = createAsyncThunk(
  "therapists/toggleTherapistActive",
  async ({ therapistId, isActive }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }

      const response = await axios.put(
        `${API_URL}/therapists/admin/${therapistId}/active`,
        { isActive },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return {
        therapistId,
        isActive,
        ...response.data,
      };
    } catch (error) {
      console.error("Toggle active error:", error.response?.data || error.message);
      return rejectWithValue(
        error.response?.data?.message || "Failed to update therapist status"
      );
    }
  }
);

// Terapist randevu istatistiklerini getir (Admin only)
export const getTherapistAppointmentStats = createAsyncThunk(
  "therapists/getAppointmentStats",
  async (therapistId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/therapists/${therapistId}/appointment-stats`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error fetching appointment stats"
      );
    }
  }
);

// Terapisti silme
export const deleteTherapist = createAsyncThunk(
  "therapists/deleteTherapist",
  async (therapistId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      await axios.delete(
        `${API_URL}/therapists/${therapistId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      return therapistId;
    } catch (error) {
      console.error("Delete error:", error.response?.data || error.message);
      return rejectWithValue(
        error.response?.data?.message || "Therapist deletion failed"
      );
    }
  }
);

// Stripe işlemleri - mevcut therapistSlice.js dosyasına eklenecek kısımlar

// Stripe hesabı oluşturma
export const createStripeAccount = createAsyncThunk(
  "therapists/createStripeAccount",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      const response = await axios.post(
        `${API_URL}/therapists/stripe/create-account`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Stripe account creation failed"
      );
    }
  }
);

// Stripe onboarding linkini alma
export const getStripeOnboardingLink = createAsyncThunk(
  "therapists/getStripeOnboardingLink",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      const response = await axios.get(
        `${API_URL}/therapists/stripe/onboarding-link`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get onboarding link"
      );
    }
  }
);

// Stripe hesap durumunu kontrol etme
export const checkStripeAccountStatus = createAsyncThunk(
  "therapists/checkStripeAccountStatus",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      const response = await axios.get(
        `${API_URL}/therapists/stripe/account-status`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to check account status"
      );
    }
  }
);

// Stripe dashboard linkini alma
export const getStripeDashboardLink = createAsyncThunk(
  "therapists/getStripeDashboardLink",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      const response = await axios.get(
        `${API_URL}/therapists/stripe/dashboard-link`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get dashboard link"
      );
    }
  }
);

// Stripe hesabını silme
export const deleteStripeAccount = createAsyncThunk(
  "therapists/deleteStripeAccount",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      const response = await axios.delete(
        `${API_URL}/therapists/stripe/account`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete Stripe account"
      );
    }
  }
);

// Ödeme geçmişini alma
export const getTherapistPaymentHistory = createAsyncThunk(
  "therapists/getPaymentHistory",
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      const response = await axios.get(
        `${API_URL}/therapists/stripe/payment-history?page=${page}&limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get payment history"
      );
    }
  }
);

// Kazanç özetini alma
export const getTherapistEarnings = createAsyncThunk(
  "therapists/getEarnings",
  async ({ startDate, endDate } = {}, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      let url = `${API_URL}/therapists/earnings`;
      const params = new URLSearchParams();
      
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get earnings data"
      );
    }
  }
);

const therapistSlice = createSlice({
  name: "therapists",
  initialState: {
    therapists: [],
    therapist: null,
    therapistDetail: null,
    loading: false,
    error: null,
    signupSuccess: false,
    loginSuccess: false,
    verificationSent: false,
    isAccountVerified: true,
    otpSent: false,
    phoneVerificationSent: false,
    phoneVerified: false,
    branches: [],
    branchesLoading: false,
    branchesError: null,
    passwordResetSent: false,
    passwordResetSuccess: false,
    stripeLoading: false,
    stripeError: null,
    stripeAccount: null,
    stripeOnboardingUrl: null,
    paymentHistory: [],
    earnings: null,
    approvalSuccess: false,
    rejectionSuccess: false,
    appointmentStatsMap: {}, // Indexed by therapistId
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setVerificationSent: (state, action) => {
      state.verificationSent = action.payload;
    },
    setOtpSent: (state, action) => {
      state.otpSent = action.payload;
    },
    setIsAccountVerified: (state, action) => {
      state.isAccountVerified = action.payload;
    },
    resetTherapists: (state) => {
      state.therapists = [];
    },
    clearTherapistDetail: (state) => {
      state.therapistDetail = null;
    },
     setPasswordResetSent: (state, action) => {
    state.passwordResetSent = action.payload;
    },
    setPasswordResetSuccess: (state, action) => {
      state.passwordResetSuccess = action.payload;
    },
    resetPasswordStates: (state) => {
      state.passwordResetSent = false;
      state.passwordResetSuccess = false;
      state.error = null;
    },
    reorderTherapists: (state, action) => {
      const { sourceIndex, destinationIndex } = action.payload;
      const result = Array.from(state.therapists);
      const [removed] = result.splice(sourceIndex, 1);
      result.splice(destinationIndex, 0, removed);
      state.therapists = result;
    },
     clearStripeError: (state) => {
      state.stripeError = null;
    },
    clearStripeOnboardingUrl: (state) => {
      state.stripeOnboardingUrl = null;
    },
    // Admin işlemleri reducers
    clearAdminError: (state) => {
      state.adminError = null;
    },
    resetAdminStates: (state) => {
      state.approvalSuccess = false;
      state.rejectionSuccess = false;
      state.adminError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Terapistleri Getirme
      .addCase(getTherapists.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTherapists.fulfilled, (state, action) => {
        state.loading = false;
        state.therapists = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(getTherapists.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Terapist Arama
      .addCase(getTherapistsSearch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTherapistsSearch.fulfilled, (state, action) => {
        state.loading = false;
        state.therapists = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(getTherapistsSearch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Tek Terapist Getirme (Detay Sayfası)
      .addCase(getTherapist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTherapist.fulfilled, (state, action) => {
        state.loading = false;
        state.therapistDetail = action.payload;
      })
      .addCase(getTherapist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Mevcut Terapist Getirme (Header için)
      .addCase(getCurrentTherapist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentTherapist.fulfilled, (state, action) => {
        state.loading = false;
        state.therapist = action.payload.therapist || action.payload;
      })
      .addCase(getCurrentTherapist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.therapist = null;
      })
      
      // Terapist Kaydı
      .addCase(registerTherapist.pending, (state) => {
        state.loading = true;
        state.signupSuccess = false;
        state.error = null;
        state.verificationSent = false;
      })
      .addCase(registerTherapist.fulfilled, (state) => {
        state.loading = false;
        state.signupSuccess = true;
        state.verificationSent = true;
      })
      .addCase(registerTherapist.rejected, (state, action) => {
        state.loading = false;
        state.signupSuccess = false;
        state.error = action.payload;
      })

      // Terapist Girişi
      .addCase(loginTherapist.pending, (state) => {
        state.loading = true;
        state.loginSuccess = false;
        state.error = null;
      })
      .addCase(loginTherapist.fulfilled, (state, action) => {
        state.loading = false;
        state.loginSuccess = true;
        state.therapist = action.payload.therapist || action.payload.user;
        state.isAccountVerified = true;
      })
      .addCase(loginTherapist.rejected, (state, action) => {
        state.loading = false;
        state.loginSuccess = false;
        state.error = action.payload;
        
        if (action.payload === "Please verify your email" ||
            action.payload === "We sent to you an email,please verify your email adress") {
          state.isAccountVerified = false;
        }
      })

      .addCase(createStripeAccount.pending, (state) => {
        state.stripeLoading = true;
        state.stripeError = null;
      })
      .addCase(createStripeAccount.fulfilled, (state, action) => {
        state.stripeLoading = false;
        state.stripeAccount = action.payload;
      })
      .addCase(createStripeAccount.rejected, (state, action) => {
        state.stripeLoading = false;
        state.stripeError = action.payload;
      })

      .addCase(getStripeOnboardingLink.pending, (state) => {
        state.stripeLoading = true;
        state.stripeError = null;
      })
      .addCase(getStripeOnboardingLink.fulfilled, (state, action) => {
        state.stripeLoading = false;
        state.stripeOnboardingUrl = action.payload.onboardingUrl;
      })
      .addCase(getStripeOnboardingLink.rejected, (state, action) => {
        state.stripeLoading = false;
        state.stripeError = action.payload;
      })

      .addCase(checkStripeAccountStatus.pending, (state) => {
        state.stripeLoading = true;
        state.stripeError = null;
      })
      .addCase(checkStripeAccountStatus.fulfilled, (state, action) => {
        state.stripeLoading = false;
        // Terapist objesindeki stripeAccount bilgisini güncelle
        if (state.therapist) {
          state.therapist.stripeAccount = {
            ...state.therapist.stripeAccount,
            ...action.payload.status
          };
        }
      })
      .addCase(checkStripeAccountStatus.rejected, (state, action) => {
        state.stripeLoading = false;
        state.stripeError = action.payload;
      })

      .addCase(getStripeDashboardLink.pending, (state) => {
        state.stripeLoading = true;
        state.stripeError = null;
      })
      .addCase(getStripeDashboardLink.fulfilled, (state, action) => {
        state.stripeLoading = false;
      })
      .addCase(getStripeDashboardLink.rejected, (state, action) => {
        state.stripeLoading = false;
        state.stripeError = action.payload;
      })

      .addCase(deleteStripeAccount.pending, (state) => {
        state.stripeLoading = true;
        state.stripeError = null;
      })
      .addCase(deleteStripeAccount.fulfilled, (state) => {
        state.stripeLoading = false;
        // Terapist objesinden stripeAccount bilgisini kaldır
        if (state.therapist) {
          state.therapist.stripeAccount = null;
        }
      })
      .addCase(deleteStripeAccount.rejected, (state, action) => {
        state.stripeLoading = false;
        state.stripeError = action.payload;
      })

      .addCase(getTherapistPaymentHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTherapistPaymentHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentHistory = action.payload.data;
      })
      .addCase(getTherapistPaymentHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getTherapistEarnings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTherapistEarnings.fulfilled, (state, action) => {
        state.stripeLoading = false;
        state.earnings = action.payload;
      })
      .addCase(getTherapistEarnings.rejected, (state, action) => {
        state.stripeLoading = false;
        state.stripeError = action.payload;
      })

      // Terapist Randevu İstatistikleri
      .addCase(getTherapistAppointmentStats.fulfilled, (state, action) => {
        const { therapistId } = action.payload;
        if (therapistId) {
          state.appointmentStatsMap[therapistId] = action.payload;
        }
      })

      // OTP Başlatma
      .addCase(initiateTherapistOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initiateTherapistOTP.fulfilled, (state) => {
        state.loading = false;
        state.otpSent = true;
      })
      .addCase(initiateTherapistOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // OTP Doğrulama
      .addCase(verifyTherapistOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyTherapistOTP.fulfilled, (state, action) => {
        state.loading = false;
        state.therapist = action.payload.user || action.payload.therapist;
        state.otpSent = false;
        state.loginSuccess = true;
      })
      .addCase(verifyTherapistOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Telefon Doğrulama Gönderme
      .addCase(sendPhoneVerification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendPhoneVerification.fulfilled, (state) => {
        state.loading = false;
        state.phoneVerificationSent = true;
      })
      .addCase(sendPhoneVerification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Telefon Doğrulama Kontrol
      .addCase(verifyPhoneToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyPhoneToken.fulfilled, (state) => {
        state.loading = false;
        state.phoneVerified = true;
        state.phoneVerificationSent = false;
      })
      .addCase(verifyPhoneToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Terapist Güncelleme
      .addCase(updateTherapist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTherapist.fulfilled, (state, action) => {
        state.loading = false;
        state.therapist = action.payload.therapist;
      })
      .addCase(updateTherapist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fotoğraf yükleme
      .addCase(uploadTherapistImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadTherapistImage.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(uploadTherapistImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // PDF yükleme
      .addCase(uploadTherapistResume.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadTherapistResume.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(uploadTherapistResume.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Şifre Değiştirme
      .addCase(changeTherapistPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changeTherapistPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changeTherapistPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(sendPasswordResetLink.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.passwordResetSent = false;
      })
      .addCase(sendPasswordResetLink.fulfilled, (state) => {
        state.loading = false;
        state.passwordResetSent = true;
      })
      .addCase(sendPasswordResetLink.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.passwordResetSent = false;
      })

      // Yeni şifre belirleme
      .addCase(setNewPasswordWithToken.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.passwordResetSuccess = false;
      })
      .addCase(setNewPasswordWithToken.fulfilled, (state) => {
        state.loading = false;
        state.passwordResetSuccess = true;
      })
      .addCase(setNewPasswordWithToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.passwordResetSuccess = false;
      })

      // Admin İşlemleri - Terapist Onaylama
      .addCase(approveTherapist.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
        state.approvalSuccess = false;
      })
      .addCase(approveTherapist.fulfilled, (state, action) => {
        state.adminLoading = false;
        state.approvalSuccess = true;
        // Terapist listesindeki ilgili terapistin durumunu güncelle
        const therapistIndex = state.therapists.findIndex(
          t => t._id === action.payload.therapistId
        );
        if (therapistIndex !== -1) {
          state.therapists[therapistIndex].isApprovedByAdmin = true;
          state.therapists[therapistIndex].isVerified = true;
          state.therapists[therapistIndex].approvalStatus = 'approved';
          state.therapists[therapistIndex].status = 'approved';
        }
        // Eğer detail sayfasındaysak o terapist de güncellensin
        if (state.therapistDetail && state.therapistDetail._id === action.payload.therapistId) {
          state.therapistDetail.isApprovedByAdmin = true;
          state.therapistDetail.isVerified = true;
          state.therapistDetail.approvalStatus = 'approved';
        }
      })
      .addCase(approveTherapist.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload;
        state.approvalSuccess = false;
      })

      // Admin İşlemleri - Terapist Reddetme
      .addCase(rejectTherapist.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
        state.rejectionSuccess = false;
      })
      .addCase(rejectTherapist.fulfilled, (state, action) => {
        state.adminLoading = false;
        state.rejectionSuccess = true;
        // Terapist listesindeki ilgili terapistin durumunu güncelle
        const therapistIndex = state.therapists.findIndex(
          t => t._id === action.payload.therapistId
        );
        if (therapistIndex !== -1) {
          state.therapists[therapistIndex].isApprovedByAdmin = false;
          state.therapists[therapistIndex].isVerified = false;
          state.therapists[therapistIndex].approvalStatus = 'rejected';
          state.therapists[therapistIndex].status = 'rejected';
        }
        // Eğer detail sayfasındaysak o terapist de güncellensin
        if (state.therapistDetail && state.therapistDetail._id === action.payload.therapistId) {
          state.therapistDetail.isApprovedByAdmin = false;
          state.therapistDetail.isVerified = false;
          state.therapistDetail.approvalStatus = 'rejected';
        }
      })
      .addCase(rejectTherapist.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload;
        state.rejectionSuccess = false;
      })

      // Terapist askıya alma / aktifleştirme
      .addCase(toggleTherapistActive.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
      })
      .addCase(toggleTherapistActive.fulfilled, (state, action) => {
        state.adminLoading = false;
        const { therapistId, isActive } = action.payload;
        const idx = state.therapists.findIndex((t) => t._id === therapistId);
        if (idx !== -1) {
          state.therapists[idx].isActive = isActive;
        }
        if (state.therapistDetail?._id === therapistId) {
          state.therapistDetail.isActive = isActive;
        }
      })
      .addCase(toggleTherapistActive.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload;
      })

      // Terapist sıralama güncelleme
      .addCase(updateTherapistOrder.rejected, (state, action) => {
        state.adminError = action.payload;
      })

      // Çıkış yapma
      .addCase(logoutTherapist.fulfilled, (state) => {
        state.therapist = null;
        state.loginSuccess = false;
        state.error = null;
      });
  },
});

export const { 
  clearError, 
  setVerificationSent, 
  setOtpSent, 
  setIsAccountVerified, 
  resetTherapists,
  clearTherapistDetail,
  setPasswordResetSent,
  setPasswordResetSuccess,
  resetPasswordStates,
  clearStripeError,
  clearStripeOnboardingUrl,
  clearAdminError,
  resetAdminStates,
  reorderTherapists
} = therapistSlice.actions;

export default therapistSlice.reducer;
